import { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, Check, Loader2, AlertCircle, Upload } from 'lucide-react';
import axios from 'axios';
import { isAdmin } from '../../lib/auth';
import { refreshCompanyBrand } from '../../lib/brandConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'name', label: 'Brand Name' },
  { key: 'acn', label: 'ACN' },
  { key: 'tagline', label: 'Tagline' },
  { key: 'director', label: 'Director' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'website', label: 'Website' },
  { key: 'address', label: 'Address' },
];

const MAX_LOGO_SIZE = 512 * 1024; // 512KB

export default function CompanyProfileCard() {
  const [fields, setFields] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const debounceTimers = useRef({});
  const fileInputRef = useRef(null);

  const admin = isAdmin();

  useEffect(() => {
    const token = localStorage.getItem('revolead_auth_token');
    fetch(`${API}/settings/company-brand`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        setFields(data);
        setLogoUrl(data.logo_url || '/LOGO.jpg');
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const saveField = useCallback(async (field, value) => {
    setSaving(field);
    setError(null);
    try {
      await axios.put(`${API}/settings/company-brand`,
        { [field]: value },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setSaved(field);
      setTimeout(() => setSaved(null), 2000);
      await refreshCompanyBrand();
      window.dispatchEvent(new CustomEvent('company-brand-updated'));
    } catch (err) {
      setError(err.response?.data?.detail || 'Save failed');
      setTimeout(() => setError(null), 3000);
    }
    setSaving(null);
  }, []);

  const saveLogoUrl = useCallback(async (url) => {
    await saveField('logo_url', url);
  }, [saveField]);

  const debouncedSave = useCallback((field, value) => {
    if (debounceTimers.current[field]) clearTimeout(debounceTimers.current[field]);
    debounceTimers.current[field] = setTimeout(() => saveField(field, value), 600);
  }, [saveField]);

  const handleChange = (field, value) => {
    setFields(prev => ({ ...prev, [field]: value }));
    if (admin) debouncedSave(field, value);
  };

  const handleBlur = (field, value) => {
    if (admin) {
      if (debounceTimers.current[field]) {
        clearTimeout(debounceTimers.current[field]);
        delete debounceTimers.current[field];
      }
      saveField(field, value);
    }
  };

  const handleLogoUrlChange = (e) => {
    const url = e.target.value;
    setLogoUrl(url);
    if (admin) {
      if (debounceTimers.current['_logo']) clearTimeout(debounceTimers.current['_logo']);
      debounceTimers.current['_logo'] = setTimeout(() => saveLogoUrl(url), 800);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_SIZE) {
      setError('File too large (max 512KB)');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const reader = new FileReader();
    reader.onloadstart = () => setLogoUploading(true);
    reader.onload = () => {
      const dataUrl = reader.result;
      setLogoUrl(dataUrl);
      setLogoUploading(false);
      setSaving('logo_url');
      saveField('logo_url', dataUrl).finally(() => setSaving(null));
    };
    reader.onerror = () => {
      setLogoUploading(false);
      setError('Failed to read file');
      setTimeout(() => setError(null), 3000);
    };
    reader.readAsDataURL(file);
  };

  if (!loaded) return null;
  if (!admin) return null;

  return (
    <div className="rounded-lg overflow-hidden" style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}>
      {/* Header */}
      <div
        className="px-6 py-3 flex items-center justify-between"
        style={{ borderBottom: '1px solid #e5e4df' }}
      >
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#c9a84c]" />
          <h2
            className="text-base font-semibold text-[#1a1a2e]"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Company Profile
          </h2>
        </div>
        {error && (
          <div className="flex items-center gap-1.5 text-xs text-[#dc2626]">
            <AlertCircle className="w-3.5 h-3.5" />
            {error}
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="px-6 py-4 grid grid-cols-2 gap-x-8 gap-y-3">
        {FIELDS.map(({ key, label }) => (
          <div key={key}>
            <label
              className="flex items-center gap-2 mb-1"
              style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              {label}
              {saving === key && <Loader2 className="w-3 h-3 animate-spin text-[#c9a84c]" />}
              {saved === key && <Check className="w-3 h-3 text-[#10b981]" />}
            </label>
            <input
              type="text"
              value={fields[key] || ''}
              onChange={e => handleChange(key, e.target.value)}
              onBlur={e => { e.target.style.borderColor = '#e5e4df'; handleBlur(key, e.target.value); }}
              onKeyDown={e => { if (e.key === 'Enter') handleBlur(key, e.target.value); }}
              className="w-full px-2.5 py-1.5 outline-none text-sm"
              style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#2c2c2c', fontFamily: "'Manrope', sans-serif" }}
              onFocus={e => { e.target.style.borderColor = '#c9a84c'; }}
            />
          </div>
        ))}

        {/* LOGO — URL + Upload */}
        <div>
          <label
            className="flex items-center gap-2 mb-1"
            style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            LOGO (URL or Upload)
            {saving === 'logo_url' && <Loader2 className="w-3 h-3 animate-spin text-[#c9a84c]" />}
            {saved === 'logo_url' && <Check className="w-3 h-3 text-[#10b981]" />}
          </label>
          <div className="flex items-start gap-3">
            <input
              type="text"
              value={logoUrl.startsWith('data:') ? '📁 Local file selected' : logoUrl}
              onChange={handleLogoUrlChange}
              onBlur={e => { e.target.style.borderColor = '#e5e4df'; if (debounceTimers.current['_logo']) { clearTimeout(debounceTimers.current['_logo']); delete debounceTimers.current['_logo']; } saveLogoUrl(logoUrl); }}
              readOnly={logoUrl.startsWith('data:')}
              className="flex-1 px-2.5 py-1.5 outline-none text-sm"
              style={{
                background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px',
                color: '#2c2c2c', fontFamily: "'Manrope', sans-serif",
                cursor: logoUrl.startsWith('data:') ? 'default' : 'text',
              }}
              onFocus={e => { e.target.style.borderColor = '#c9a84c'; }}
              onBlur={e => { e.target.style.borderColor = '#e5e4df'; }}
              placeholder="/LOGO.jpg or upload a file"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={logoUploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-colors"
              style={{
                background: '#1a1a2e', color: '#f8f7f4',
                opacity: logoUploading ? 0.6 : 1, cursor: logoUploading ? 'not-allowed' : 'pointer',
              }}
            >
              {logoUploading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5" />
              )}
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
            />
            <img
              src={logoUrl}
              alt="Logo preview"
              style={{ maxWidth: 40, maxHeight: 40, objectFit: 'contain', flexShrink: 0 }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
